'use client';

import React, { Fragment, useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  Box,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { AppWindow } from '@phosphor-icons/react/dist/ssr/AppWindow';
import { ArrowsDownUp } from '@phosphor-icons/react/dist/ssr/ArrowsDownUp';

import { ColumnType, DynamicTableProps } from '@/types/general';

import { DateCell } from './date-cell';
import { DetailsDrawerCell } from './details-drawer-cell';
import { FormattedDateTime } from './formatted-date';
import { OperatorCell } from './operator-cell';
import { PlayerCell } from './player-cell';
import { StatusChip } from './status-chip';
import { TimeFilterMenu } from './time-filter';

const renderColumn = (type: ColumnType): React.ReactNode => {
  if (type.id) return type.id.content;
  if (type.text)
    return (
      <Box>
        <Typography sx={{ textWrap: 'nowrap' }} variant="body2">
          {type.text.content}
        </Typography>
        {type.text.subtitle ? <Typography variant="caption">{type.text.subtitle}</Typography> : null}
      </Box>
    );
  if (type.component) return type.component;
  if (type.link)
    return (
      <IconButton component={Link} href={type.link}>
        <AppWindow />
      </IconButton>
    );
  if (type.amountWithCurrency) {
    return (
      <Typography sx={{ textWrap: 'nowrap' }} variant="body2">
        {type.amountWithCurrency.amount} {type.amountWithCurrency.currency}
      </Typography>
    );
  }
  if (type.operator)
    return (
      <OperatorCell
        opId={type.operator.opId}
        opTitle={type.operator.opTitle || ''}
        parentOpId={type.operator.parentOpId}
        parentOpTitle={type.operator.parentOpTitle}
      />
    );
  if (type.status) return <StatusChip status={type.status} />;
  if (type.time) return <DateCell date={type.time} />;
  if (type.detailsDrawer)
    return <DetailsDrawerCell drawerState={type.detailsDrawer.drawerState} handleClick={type.detailsDrawer.onClick} />;
  if (type.player)
    return (
      <PlayerCell
        playerCode={type.player.playerCode}
        playerId={type.player.playerId}
        profile={type.player.profile}
        isVerified={type.player.isVerified}
        isTest={type.player.isTest}
        username={type.player.username}
        showCopyButton
      />
    );
  return null;
};

export function DynamicTable({
  headers,
  rows,
  onSortChange,
  sortField,
  sortDirection = 'asc',
  pagination,
  hidePagination,
  maxHeight = 840,
  isLoading = true,
  lastFetchTime,
  quickFilters,
}: DynamicTableProps): React.JSX.Element {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const handleSort = (key: string): void => {
    if (!onSortChange) return;
    const newDirection = sortField === key && sortDirection === 'asc' ? 'desc' : 'asc';
    onSortChange(key, newDirection);
  };

  const [currentPage, setCurrentPage] = useState(pagination?.page || 1);
  const updateUrl = (page: number): void => {
    const updatedSearchParams = new URLSearchParams(Array.from(searchParams.entries()));
    updatedSearchParams.set('page', page.toString());
    updatedSearchParams.set('limit', pagination?.limit?.toString() || '50');
    router.push(`${pathname}?${updatedSearchParams.toString()}`);
  };
  return (
    <Box overflow="hidden">
      {quickFilters ?? null}
      <FormattedDateTime timestamp={lastFetchTime} variant="horizontal" isLoading={isLoading} />
      <TableContainer sx={{ height: maxHeight, overflowY: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {headers.map((header, index: number) => (
                <TableCell
                  key={index}
                  sx={{
                    py: 1.5,
                    minWidth: header.fixedWidth || 0,
                    // backgroundColor: 'var(--mui-palette-primary-800) !important',
                    backgroundColor: 'var(--mui-palette-background-level1) !important',
                    color: 'var(--mui-palette-text-primary) !important',
                    textWrap: 'nowrap',
                    whiteSpace: 'nowrap',
                    display: {
                      xs: header.hideOnMobile ? 'none' : 'table-cell',
                      sm: 'table-cell',
                    },
                    textAlign: header.align || 'left',
                    borderRight: index !== headers.length - 1 ? '1px solid #B3B9C6' : 'none',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="subtitle1" sx={{ width: 1 }}>
                      {header.label}
                    </Typography>
                    {header.sortable ? (
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (header.key) {
                            handleSort(header.key);
                          }
                        }}
                      >
                        {sortField === header.key ? (
                          sortDirection === 'asc' ? (
                            <ArrowsDownUp fontSize="inherit" />
                          ) : (
                            <ArrowsDownUp fontSize="inherit" />
                          )
                        ) : (
                          <ArrowsDownUp fontSize="inherit" />
                        )}
                      </IconButton>
                    ) : null}
                  </Stack>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* {isLoading ? (
              <TableRow>
                <TableCell colSpan={headers.length} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={headers.length} align="center">
                  <ItemsNotFound />
                </TableCell>
              </TableRow>
            ) : ( */}
            {rows.map((row) => (
              <TableRow key={row.id} sx={{ background: row.background }}>
                {row.row.map((col, idx) => (
                  <TableCell
                    key={idx}
                    sx={{
                      minWidth: col.fixedWidth || 120,
                      display: {
                        xs: col.hideOnMobile ? 'none' : 'table-cell',
                        sm: 'table-cell',
                      },
                      textAlign: col.textAlign || 'left',
                    }}
                  >
                    {renderColumn(col.type)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!hidePagination && !isLoading ? (
        <Fragment>
          <Divider />
          <TablePagination
            component="div"
            count={pagination?.count || 0}
            onPageChange={(_, page) => {
              setCurrentPage(page + 1);
              updateUrl(page + 1);
            }}
            page={currentPage - 1}
            rowsPerPage={pagination?.limit || 50}
            rowsPerPageOptions={[]}
          />
        </Fragment>
      ) : null}
    </Box>
  );
}
